export type ApiDiagnosticInfo = {
    errorId: string;
    timestamp: string;

    pageUrl: string;
    apiUrl?: string;

    errorType: string;
    message: string;

    status?: number | null;
    statusText?: string | null;

    browser: string;
    userAgent: string;
    language: string;
    languages: readonly string[];

    platform: string;
    online: boolean;

    durationMs?: number | null;
};

function createErrorId() {
    const date = new Date()
        .toISOString()
        .slice(0, 10)
        .replace(/-/g, "");

    const random = Math.random()
        .toString(36)
        .substring(2, 8)
        .toUpperCase();

    return `${date}-${random}`;
}

function getBrowserName(userAgent: string) {
    if (/Edg\//i.test(userAgent)) return "Microsoft Edge";
    if (/OPR\//i.test(userAgent)) return "Opera";
    if (/CriOS\//i.test(userAgent)) return "Chrome iOS";
    if (/Chrome\//i.test(userAgent)) return "Google Chrome";
    if (/FxiOS\//i.test(userAgent)) return "Firefox iOS";
    if (/Firefox\//i.test(userAgent)) return "Mozilla Firefox";

    if (
        /Safari\//i.test(userAgent) &&
        !/Chrome\//i.test(userAgent) &&
        !/Chromium\//i.test(userAgent)
    ) {
        return "Safari";
    }

    return "Unknown browser";
}

export function createApiDiagnostic(
    error: any,
    options?: {
        apiUrl?: string;
        durationMs?: number | null;
    }
): ApiDiagnosticInfo {
    const userAgent = navigator.userAgent;

    let errorType = "UNKNOWN_ERROR";

    if (error?.name === "AbortError") {
        errorType = "TIMEOUT";
    } else if (error?.status) {
        errorType = "HTTP_ERROR";
    } else if (
        error instanceof TypeError ||
        error?.message?.toLowerCase()?.includes("failed to fetch")
    ) {
        errorType = "NETWORK_ERROR";
    }

    const userAgentData = (navigator as any).userAgentData;

    return {
        errorId: createErrorId(),

        timestamp: new Date().toISOString(),

        pageUrl: window.location.href,
        apiUrl: options?.apiUrl,

        errorType,

        message:
            error?.message ??
            error?.statusText ??
            String(error),

        status:
            error?.status ??
            error?.response?.status ??
            null,

        statusText:
            error?.statusText ??
            error?.response?.statusText ??
            null,

        browser: getBrowserName(userAgent),

        userAgent,

        language: navigator.language,
        languages: navigator.languages,

        platform:
            userAgentData?.platform ??
            navigator.platform ??
            "Unknown",

        online: navigator.onLine,

        durationMs: options?.durationMs ?? null,
    };
}

export function diagnosticToText(
    diagnostic: ApiDiagnosticInfo
) {
    return `
BadmintonPlayer Event - Diagnostic information
================================================

Error ID: ${diagnostic.errorId}
Time: ${diagnostic.timestamp}

ERROR
------------------------------------------------
Type: ${diagnostic.errorType}
Message: ${diagnostic.message}
HTTP status: ${diagnostic.status ?? "No response"}
HTTP status text: ${diagnostic.statusText ?? "-"}

REQUEST
------------------------------------------------
Page: ${diagnostic.pageUrl}
API: ${diagnostic.apiUrl ?? "Unknown"}
Duration: ${
        diagnostic.durationMs != null
            ? `${diagnostic.durationMs} ms`
            : "-"
    }

DEVICE / BROWSER
------------------------------------------------
Browser: ${diagnostic.browser}
Platform: ${diagnostic.platform}
Language: ${diagnostic.language}
Languages: ${diagnostic.languages.join(", ")}
Online: ${diagnostic.online ? "Yes" : "No"}

User Agent:
${diagnostic.userAgent}

================================================
Please send this information to event@badminton.dk
`.trim();
}